import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../styles/lib/utils';
import { Icon } from '../Icon';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { isDarkHexColor, isFullHexColor, normalizeHexColor } from './hexColor';

const swatchVariants = cva('shrink-0 rounded-[4px] border border-grey-30 cursor-pointer', {
  variants: {
    size: {
      small: 'h-5 w-5',
      medium: 'h-6 w-6',
    },
  },
  defaultVariants: {
    size: 'medium',
  },
});

interface ColorSwatchGroup {
  label: string;
  colors: readonly string[];
  /** Optional caveat shown under the group heading, e.g. a palette-safety note. */
  description?: string;
}

interface ColorSwatchPickerProps
  extends Omit<React.ComponentProps<'button'>, 'value' | 'onChange' | 'color'>,
    VariantProps<typeof swatchVariants> {
  value: string;
  onChange: (color: string) => void;
  /** The colors offered as one-click shortcuts, in the order and grouping they should appear. */
  groups: readonly ColorSwatchGroup[];
  /** Names the color being picked, for the trigger's accessible name and the popover heading. */
  label: string;
  /** Each swatch gets `${swatchTestIdPrefix}-${color}` as its test id. */
  swatchTestIdPrefix: string;
  /** Adds a free-form section: a native color input plus a hex field, alongside `groups`. */
  allowCustomColor?: boolean;
}

/**
 * A trigger swatch that opens grouped grids of colors to choose from.
 *
 * `groups` are one-click shortcuts, not a constraint: with `allowCustomColor` the popover also
 * offers a native picker and a hex field, so any color is reachable.
 */
function ColorSwatchPicker({
  value,
  onChange,
  groups,
  label,
  size,
  className,
  swatchTestIdPrefix,
  allowCustomColor,
  ...props
}: ColorSwatchPickerProps) {
  const [open, setOpen] = React.useState(false);
  const [hexDraft, setHexDraft] = React.useState(value);

  // A preset click changes `value` from outside, but a draft that already means `value` is left
  // alone so the expansion of what the user just typed can't overwrite what they are typing.
  React.useEffect(() => {
    setHexDraft((draft) => (normalizeHexColor(draft) === value ? draft : value));
  }, [value]);

  const handleHexInput = (next: string) => {
    setHexDraft(next);
    const normalized = normalizeHexColor(next);
    // Shorthand expands on blur only; expanding mid-typing would fill the field to `maxLength`.
    if (normalized && isFullHexColor(next)) onChange(normalized);
  };

  const handleHexBlur = () => {
    const normalized = normalizeHexColor(hexDraft);
    setHexDraft(normalized ?? value);
    if (normalized) onChange(normalized);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-slot="color-swatch-picker"
          aria-label={label}
          className={cn(
            'flex shrink-0 cursor-pointer items-center gap-1.5 rounded-[6px] border border-grey-30 px-1.5 py-1 hover:border-grey-50 hover:bg-grey-10 focus-ring',
            className,
          )}
          {...props}
        >
          <span className={cn(swatchVariants({ size }), 'cursor-pointer')} style={{ backgroundColor: value }} />
          <Icon icon="chevronDown" size={12} className="text-grey-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" data-slot="color-swatch-picker-content">
        <div className="mb-2 font-noto text-xs font-medium text-primary">{label}</div>
        <div className="flex flex-col gap-3">
          {groups.map((group) => (
            <div key={group.label} className="flex flex-col gap-1.5">
              <div className="font-noto text-xs font-medium text-grey-50">{group.label}</div>
              {group.description && (
                <div className="font-noto text-[11px] leading-tight text-grey-50">{group.description}</div>
              )}
              <div className="grid grid-cols-6 gap-1.5">
                {group.colors.map((color) => (
                  <button
                    key={color}
                    type="button"
                    aria-label={color}
                    aria-pressed={color === value}
                    onClick={() => {
                      onChange(color);
                      setOpen(false);
                    }}
                    className={cn(
                      swatchVariants({ size }),
                      'flex items-center justify-center hover:border-grey-50 focus-ring',
                    )}
                    style={{ backgroundColor: color }}
                    data-testid={`${swatchTestIdPrefix}-${color}`}
                  >
                    {color === value && (
                      <Icon
                        icon="luCheck"
                        size={size === 'small' ? 12 : 14}
                        className={cn('stroke-3', isDarkHexColor(color) ? 'text-white' : 'text-grey-90')}
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {allowCustomColor && (
            <div className="flex flex-col gap-1.5">
              <div className="font-noto text-xs font-medium text-grey-50">Custom color</div>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  aria-label="Custom color"
                  value={normalizeHexColor(hexDraft) ?? value}
                  onChange={(e) => handleHexInput(e.target.value)}
                  className={cn(
                    swatchVariants({ size }),
                    'focus-ring appearance-none bg-transparent p-0 [&::-webkit-color-swatch]:rounded-[3px] [&::-webkit-color-swatch]:border-0 [&::-webkit-color-swatch-wrapper]:p-0',
                  )}
                  data-testid={`${swatchTestIdPrefix}-custom-native`}
                />
                <input
                  type="text"
                  aria-label="Custom color hex"
                  value={hexDraft}
                  spellCheck={false}
                  maxLength={7}
                  placeholder="#000000"
                  onChange={(e) => handleHexInput(e.target.value)}
                  onBlur={handleHexBlur}
                  className={cn(
                    'w-24 rounded-[4px] border px-1.5 py-1 font-mono text-xs outline-none',
                    // The error border can't also show focus, so an invalid draft draws the ring
                    normalizeHexColor(hexDraft)
                      ? 'border-grey-30 focus-visible:border-focus'
                      : 'border-red-50 focus-ring',
                  )}
                  data-testid={`${swatchTestIdPrefix}-custom-hex`}
                />
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { ColorSwatchPicker, swatchVariants, type ColorSwatchGroup };
